import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { companyAPI } from "@/services/companyService";

interface BrandingState {
  name: string;
  nombre_legal: string;
  email: string;
  phone: string;
  website: string;
  address: string;
  logo_url: string;
  primary_color: string;
  secondary_color: string;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  lastUpdated: number | null;
}

const DEFAULT_BRANDING = {
  name: "",
  nombre_legal: "",
  email: "",
  phone: "",
  website: "",
  address: "",
  logo_url: "",
  primary_color: "#3b82f6", // Default blue
  secondary_color: "#64748b", // Default slate
};

const getInitialBranding = (): BrandingState => {
  const cached = localStorage.getItem("companyBranding");
  if (cached) {
    try {
      const parsed = JSON.parse(cached);
      return {
        ...DEFAULT_BRANDING,
        ...parsed,
        status: "idle",
        error: null,
        lastUpdated: parsed.updated_at || null,
      };
    } catch (e) {
      console.error("Error parsing cached branding", e);
    }
  }
  return {
    ...DEFAULT_BRANDING,
    status: "idle",
    error: null,
    lastUpdated: null,
  };
};

export const fetchBranding = createAsyncThunk(
  "branding/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const response = await companyAPI.getSettings();
      // Assuming getSettings returns the object with branding info
      // based on the user's diagnosis: { logo_url, primary_color, secondary_color }
      const data = response.data || response;
      
      const brandingData = {
        name: data.name || "",
        nombre_legal: data.nombre_legal || "",
        email: data.email || "",
        phone: data.phone || "",
        website: data.website || "",
        address: data.address || "",
        logo_url: data.logo_url || "",
        primary_color: data.primary_color || DEFAULT_BRANDING.primary_color,
        secondary_color: data.secondary_color || DEFAULT_BRANDING.secondary_color,
        updated_at: Date.now(),
      };

      localStorage.setItem("companyBranding", JSON.stringify(brandingData));
      return brandingData;
    } catch (error: any) {
      return rejectWithValue(error.message || "Error al obtener branding");
    }
  }
);

const brandingSlice = createSlice({
  name: "branding",
  initialState: getInitialBranding(),
  reducers: {
    setBranding: (state, action: PayloadAction<Partial<BrandingState>>) => {
      if (action.payload.name !== undefined) state.name = action.payload.name;
      if (action.payload.nombre_legal !== undefined) state.nombre_legal = action.payload.nombre_legal;
      if (action.payload.email !== undefined) state.email = action.payload.email;
      if (action.payload.phone !== undefined) state.phone = action.payload.phone;
      if (action.payload.website !== undefined) state.website = action.payload.website;
      if (action.payload.address !== undefined) state.address = action.payload.address;
      if (action.payload.logo_url !== undefined) state.logo_url = action.payload.logo_url;
      if (action.payload.primary_color !== undefined) state.primary_color = action.payload.primary_color;
      if (action.payload.secondary_color !== undefined) state.secondary_color = action.payload.secondary_color;
      
      state.lastUpdated = Date.now();
      
      localStorage.setItem("companyBranding", JSON.stringify({
        name: state.name,
        nombre_legal: state.nombre_legal,
        email: state.email,
        phone: state.phone,
        website: state.website,
        address: state.address,
        logo_url: state.logo_url,
        primary_color: state.primary_color,
        secondary_color: state.secondary_color,
        updated_at: state.lastUpdated,
      }));
    },
    clearBranding: (state) => {
      state.logo_url = DEFAULT_BRANDING.logo_url;
      state.primary_color = DEFAULT_BRANDING.primary_color;
      state.secondary_color = DEFAULT_BRANDING.secondary_color;
      state.status = "idle";
      state.lastUpdated = null;
      localStorage.removeItem("companyBranding");
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranding.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchBranding.fulfilled, (state, action: PayloadAction<any>) => {
        state.status = "succeeded";
        state.name = action.payload.name;
        state.nombre_legal = action.payload.nombre_legal;
        state.email = action.payload.email;
        state.phone = action.payload.phone;
        state.website = action.payload.website;
        state.address = action.payload.address;
        state.logo_url = action.payload.logo_url;
        state.primary_color = action.payload.primary_color;
        state.secondary_color = action.payload.secondary_color;
        state.lastUpdated = action.payload.updated_at;
        state.error = null;
      })
      .addCase(fetchBranding.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload as string;
      });
  },
});

export const { setBranding, clearBranding } = brandingSlice.actions;
export default brandingSlice.reducer;
